import {
  Badge,
  Button,
  Card,
  ContextMenu,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
  Typography,
} from "@/components/ui";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import type { ObjectUpdate } from "@/lib/data/schemas/updates/ObjectUpdateSchema";
import { formatDateTime, formatUsersName } from "@/lib/utils/format";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import {
  ObjectUpdateComposer,
  type UpdateComposerState,
} from "./ObjectUpdateComposer";

export function ObjectUpdateCard({
  update,
  onDelete,
  onComposerStateChange,
  onEditingChange,
}: {
  update: ObjectUpdate;
  onDelete: () => void;
  onComposerStateChange: (id: string, state: UpdateComposerState) => void;
  onEditingChange: (update: ObjectUpdate, editing: boolean) => void;
}) {
  const isExpense = update.anchorObjectType === "matter_expense";
  const [isEditing, setIsEditing] = useState(false);
  const reportComposerState = useCallback(
    (state: UpdateComposerState) => {
      onComposerStateChange(update.id, state);
    },
    [onComposerStateChange, update.id],
  );
  const onEdit = () => {
    if (update.canEdit) {
      onEditingChange(update, true);
      setIsEditing(true);
    }
  };
  const finishEditing = () => {
    setIsEditing(false);
    onEditingChange(update, false);
  };

  const card = (
    <Card className="space-y-3 p-4 shadow-none">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-muted-foreground shrink-0">
            {isExpense ? "Cost" : "Matter"}
          </Badge>
          {isExpense && update.anchorLabel ? (
            <Text variant="body" className="min-w-0 break-words">
              {update.anchorLabel}
            </Text>
          ) : null}
        </div>
        {!isEditing && (update.canEdit || update.canDelete) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Update actions"
                className="h-7 w-7 shrink-0"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {update.canEdit ? (
                <DropdownMenuItem onSelect={onEdit}>
                  <Pencil />
                  Edit update
                </DropdownMenuItem>
              ) : null}
              {update.canDelete ? (
                <DropdownMenuItem
                  onSelect={onDelete}
                  className="text-destructive"
                >
                  <Trash2 />
                  Delete update
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {isEditing ? (
        <ObjectUpdateComposer
          key={update.id}
          anchorObjectType={update.anchorObjectType}
          anchorObjectId={update.anchorObjectId}
          update={update}
          autoFocus
          disabled={!update.canEdit}
          onSaved={finishEditing}
          onCancel={finishEditing}
          onStateChange={reportComposerState}
        />
      ) : (
        <Text
          variant="body"
          className="font-normal break-words whitespace-pre-wrap"
        >
          {update.message}
        </Text>
      )}
      <div className="border-border/60 flex flex-col gap-3 border-t pt-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Text variant="caption">At time of update</Text>
          <Badge
            variant="secondary"
            className="max-w-full gap-1 font-normal whitespace-normal"
          >
            <span className="text-muted-foreground shrink-0">
              Matter status:
            </span>
            <span className="min-w-0 font-medium [overflow-wrap:anywhere]">
              {update.matterSnapshot.statusLabelAtCreation}
            </span>
          </Badge>
          {isExpense && update.expenseSnapshot ? (
            <Badge
              variant="secondary"
              className="max-w-full gap-1 font-normal whitespace-normal"
            >
              <span className="text-muted-foreground shrink-0">
                Paid status:
              </span>
              <span className="min-w-0 font-medium [overflow-wrap:anywhere]">
                {update.expenseSnapshot.paidStatusAtCreation ?? "Not set"}
              </span>
            </Badge>
          ) : null}
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <UserProfilePhoto
            className="h-4 w-4 shrink-0"
            initialsClassName="text-[8px]"
            user={update.creator}
          />
          <Typography
            variant="subHeading"
            size="xs"
            className="min-w-0 break-words"
          >
            {formatUsersName(update.creator)} •{" "}
            <time dateTime={update.dateCreated}>
              {formatDateTime(update.dateCreated, false)}
            </time>
          </Typography>
        </div>
      </div>
    </Card>
  );

  return (
    <ContextMenu
      disabled={isEditing}
      entityName="update"
      trigger={card}
      open={update.canEdit ? { onSelect: onEdit } : false}
      delete={update.canDelete ? { onSelect: onDelete } : false}
      copy={{ value: update.message, successLabel: "Update contents" }}
    />
  );
}
